import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import ErrorBoundary from "@/components/errors/ErrorBoundary";
import GlobalErrorDialog from "@/components/errors/GlobalErrorDialog";
import { formatError } from "@/lib/formatError";

interface GlobalErrorContextValue {
  reportError: (error: unknown) => void;
}

const GlobalErrorContext = createContext<GlobalErrorContextValue | null>(null);

export function useGlobalError(): GlobalErrorContextValue {
  const ctx = useContext(GlobalErrorContext);
  if (!ctx) {
    throw new Error("useGlobalError must be used within GlobalErrorHandler");
  }
  return ctx;
}

interface GlobalErrorHandlerProps {
  children: ReactNode;
}

const DEDUPE_WINDOW_MS = 1500;

/** Must match `PLUGIN_ERROR_EVENT` in `@coti-io/coti-wallet-plugin`. */
const PLUGIN_ERROR_EVENT = "coti-wallet-plugin:error";

function errorToText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object") {
    const maybeMessage = (error as { message?: unknown }).message;
    if (typeof maybeMessage === "string") {
      const maybeCode = (error as { code?: unknown }).code;
      return typeof maybeCode === "number" || typeof maybeCode === "string"
        ? `${maybeCode} ${maybeMessage}`
        : maybeMessage;
    }
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error ?? "");
}

const EXTENSION_FRAME = /(?:chrome|moz|safari-web)-extension:\/\//;

function stackFrames(error: unknown): string[] {
  const stack = (error as { stack?: unknown } | null | undefined)?.stack;
  if (typeof stack !== "string") return [];
  return stack.split("\n").filter((line) => /^\s*at\s/.test(line));
}

/**
 * True when every frame comes from a browser-extension bundle.
 * Wallet extensions throw unhandled rejections into the page — OneKey's
 * IndexedDB teardown error fires on every load when several wallets are
 * installed, and it must not open the global error dialog.
 */
function isExtensionOriginatedError(error: unknown): boolean {
  const frames = stackFrames(error);
  return frames.length > 0 && frames.every((line) => EXTENSION_FRAME.test(line));
}

function isIgnorableError(error: unknown): boolean {
  if (isExtensionOriginatedError(error)) return true;

  const text = errorToText(error);

  return (
    /user rejected|user denied|rejected the request|request rejected/i.test(text) ||
    /ResizeObserver loop/i.test(text) ||
    /^Script error\.?$/i.test(text.trim()) ||
    /wallet_revokePermissions/i.test(text) ||
    (/-32601/.test(text) && /method does not exist|is not available|method not found/i.test(text)) ||
    // Zerion proxies wallet_addEthereumChain/chain validation for Sepolia through
    // its own backend rather than the dApp-provided RPC URLs; a transient 404 there
    // surfaces as this plain (stack-less) rejection and doesn't block the switch.
    (/-32000/.test(text) && /request failed with status code 404/i.test(text))
  );
}

/** Matches plugin `CotiErrorCode.RPC_RATE_LIMITED` and raw Fuji/public RPC 429 payloads. */
function isRpcRateLimitError(error: unknown): boolean {
  if (error && typeof error === "object" && (error as { code?: unknown }).code === "RPC_RATE_LIMITED") {
    return true;
  }
  const text = errorToText(error).toLowerCase();
  return (
    text.includes("rate limited")
    || text.includes("rate limit")
    || text.includes("too many requests")
    || text.includes("-32005")
    || /\b429\b/.test(text)
  );
}

const GlobalErrorHandler = ({ children }: GlobalErrorHandlerProps) => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState<string | null>(null);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [requireReload, setRequireReload] = useState(false);
  const lastKeyRef = useRef<string | null>(null);
  const lastAtRef = useRef(0);

  const reportError = useCallback((error: unknown) => {
    if (isIgnorableError(error)) return;

    const formatted = formatError(error);
    const rateLimited = isRpcRateLimitError(error);
    const now = Date.now();
    if (
      lastKeyRef.current === formatted.details &&
      now - lastAtRef.current < DEDUPE_WINDOW_MS
    ) {
      return;
    }

    lastKeyRef.current = formatted.details;
    lastAtRef.current = now;
    setDetails(formatted.details);
    if (rateLimited) {
      setTitle("Network rate limited");
      setDescription(
        formatted.message.includes("reload")
          ? formatted.message
          : "Avalanche Fuji RPC is rate limited. Please reload the page and try again.",
      );
      setRequireReload(true);
    } else {
      setTitle(undefined);
      setDescription(undefined);
      setRequireReload(false);
    }
    setOpen(true);

    console.error("[GlobalErrorHandler]", error);
  }, []);

  useEffect(() => {
    const onWindowError = (event: ErrorEvent) => {
      if (event.error) {
        reportError(event.error);
      } else if (event.message) {
        reportError(event.message);
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      reportError(event.reason ?? "Unhandled promise rejection");
    };

    const onPluginError = (event: Event) => {
      reportError((event as CustomEvent<unknown>).detail ?? "Plugin error");
    };

    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener(PLUGIN_ERROR_EVENT, onPluginError);

    return () => {
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener(PLUGIN_ERROR_EVENT, onPluginError);
    };
  }, [reportError]);

  const value = useMemo(() => ({ reportError }), [reportError]);

  return (
    <GlobalErrorContext.Provider value={value}>
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.error("[ErrorBoundary]", error, errorInfo);
        }}
      >
        {children}
      </ErrorBoundary>
      <GlobalErrorDialog
        open={open}
        title={title}
        description={description}
        details={details}
        requireReload={requireReload}
        onOpenChange={(next) => {
          if (!next && requireReload) return;
          setOpen(next);
        }}
        onReload={() => window.location.reload()}
      />
    </GlobalErrorContext.Provider>
  );
};

export default GlobalErrorHandler;
